/**
 * CatalogPage: Main page showing the grid of plugins with search
 */

import React, { useEffect, useState } from "react";
import { getMarketplaceClient } from "../lib/marketplace-client";
import { filterPlugins } from "../lib/search";
import PluginCard from "../components/PluginCard";
import SearchBar from "../components/SearchBar";
import Loading from "../components/Loading";
import type { MarketplaceEntry, SearchState } from "../types/index";

interface CatalogPageProps {
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
}

export default function CatalogPage({ onSelectPlugin }: CatalogPageProps): JSX.Element {
  const [state, setState] = useState<SearchState>({
    query: "",
    results: [],
    isLoading: true,
    error: null,
  });
  const [allPlugins, setAllPlugins] = useState<MarketplaceEntry[]>([]);

  // Load marketplace on mount
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const client = getMarketplaceClient();
        const catalog = await client.getCatalog();
        setAllPlugins(catalog.plugins);
        setState((s) => ({
          ...s,
          results: catalog.plugins,
          isLoading: false,
        }));
      } catch (error) {
        console.error("Failed to load catalog:", error);
        setState((s) => ({
          ...s,
          isLoading: false,
          error: error instanceof Error ? error.message : "Failed to load catalog",
        }));
      }
    };

    loadCatalog();
  }, []);

  const handleSearch = (query: string) => {
    setState((s) => ({
      ...s,
      query,
      results: filterPlugins(allPlugins, {
        query,
        sortBy: "name",
      }),
    }));
  };

  if (state.error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h1>Error Loading Marketplace</h1>
        <p style={{ color: "red" }}>{state.error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "0.5rem 1rem",
            fontSize: "1rem",
            backgroundColor: "#0078d4",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: "2rem" }}>
        <h1>Skills Marketplace</h1>
        <p style={{ color: "#666", marginBottom: "1rem" }}>
          Browse and discover available AI skills for Claude Code, Cowork, and GitHub Copilot.
        </p>
        <p style={{ color: "#999", fontSize: "0.9rem" }}>
          Total available: {allPlugins.length} skills
        </p>
      </div>

      <SearchBar onSearch={handleSearch} isLoading={state.isLoading} />

      {state.isLoading && !allPlugins.length ? (
        <Loading message="Loading skills..." />
      ) : state.results.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p style={{ fontSize: "1.1rem", color: "#666" }}>
            {state.query ? `No skills match "${state.query}"` : "No skills available"}
          </p>
          {state.query && (
            <button
              onClick={() => handleSearch("")}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: "#f0f0f0",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {state.results.map((plugin) => (
            <PluginCard
              key={`${plugin.source}#${plugin.orgOrOwner}/${plugin.repoName}`}
              plugin={plugin}
              onClick={() => onSelectPlugin(plugin)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
